import { Paths } from '@call-point/relate-cx-common/dist/common.types';
import { AddressInput } from '@call-point/relate-cx-common/dist/documents/address/address.types';

import {
  CompanyParams as Api_CompanyParams,
  CompanyListDTO as Api_CompanyListDTO,
  CompanyListNameDTO as Api_CompanyListNameDTO,
  CompanyDetailDTO as Api_CompanyDetailDTO,
  CompanyUpdateApiDTO as Api_CompanyUpdateApiDTO,
  CompanyCreateApiDTO as Api_CompanyCreateApiDTO
} from '@call-point/relate-cx-common/dist/documents/company/company.api.schema';
import { WaivableStatusEnum } from '@call-point/relate-cx-common/dist/documents/companyWindow/companyWindow.types';
import {
  UserParams as Api_UserParams,
  UserDetailDTO as Api_UserDetailDTO
} from '@call-point/relate-cx-common/dist/documents/user/user.api.schema';
import { RoleEnum } from '@call-point/relate-cx-common/dist/documents/user/user.repo.schema';
import { ContactCustomerDTO as Api_ContactCustomerDTO } from '@call-point/relate-cx-common/dist/provider/poolBrain/customer/contactCustomer.schema';
import { CustomerDetailDTO as Api_CustomerDetailDTO } from '@call-point/relate-cx-common/dist/provider/poolBrain/customer/customerDetail.schema';
import { CustomerLocationDTO as Api_CustomerLocationDTO } from '@call-point/relate-cx-common/dist/provider/poolBrain/location/location.schema';

import { arrayOfAll } from '@call-point/relate-cx-common/dist/utils/type.utils';

import type { PagingParams as Api_PagingParams } from '@call-point/relate-cx-common/dist/api/common.queryParam.types';
import type {
  AccountResponseDTO as Api_AccountResponseDTO,
  AccountParams as Api_AccountParams
} from '@call-point/relate-cx-common/dist/documents/account/account.api.schema';
import type {
  AiCallForCallPopDTO as Api_AiCallForCallPopDTO,
  AiCallDetailDTO as Api_AiCallDetailDTO,
  AiCallListDTO as Api_AiCallListDTO,
  AiCallParams as Api_AiCallParams
} from '@call-point/relate-cx-common/dist/documents/aiCall/aiCall.api.schema';
import type {
  AppointmentDetailDTO as Api_AppointmentDetailDTO,
  AppointmentListDTO as Api_AppointmentListDTO,
  AppointmentParams as Api_AppointmentParams
} from '@call-point/relate-cx-common/dist/documents/appointment/appointment.api.schema';
import type {
  AppointmentHistoryDetailDTO as Api_AppointmentHistoryDetailDTO,
  AppointmentHistoryParams as Api_AppointmentHistoryParams
} from '@call-point/relate-cx-common/dist/documents/appointmentHistory/appointmentHistory.api.schema';
import type {
  AppointmentWindowListDTO as Api_AppointmentWindowListDTO,
  AppointmentWindowParams as Api_AppointmentWindowParams
} from '@call-point/relate-cx-common/dist/documents/appointmentWindow/appointmentWindow.api.schema';

import type {
  CallCenterDetailDTO as Api_CallCenterDetailDTO,
  CallCenterListDTO as Api_CallCenterListDTO,
  CallCenterCreateDTO as Api_CallCenterCreateDTO,
  CallCenterUpdateDTO as Api_CallCenterUpdateDTO,
  CallCenterParams as Api_CallCenterParams
} from '@call-point/relate-cx-common/dist/documents/callCenter/callCenter.api.schema';
import type {
  CallSegmentListItemDTO as Api_CallSegmentListItemDTO,
  CallSegmentListParams as Api_CallSegmentListParams
} from '@call-point/relate-cx-common/dist/documents/callSegment/callSegment.api.schema';
import type {
  Company as Api_Company,
  ServiceTitanApiCompany as Api_ServiceTitanApiCompany,
  StCapacityCompany as Api_StCapacityCompany,
  ServiceTitanPortalV2Company as Api_ServiceTitanPortalV2Company,
  PoolBrainCompany as Api_PoolBrainCompany
} from '@call-point/relate-cx-common/dist/documents/company/company.repo.schema';
import type {
  CompanyWindowListDTO as Api_CompanyWindowListDTO,
  CompanyWindowDetailDTO as Api_CompanyWindowDetailDTO,
  CompanyWindowParams as Api_CompanyWindowParams
} from '@call-point/relate-cx-common/dist/documents/companyWindow/companyWindow.api.schema';
import type {
  CustomerDetailDTO as Api_PointmanCustomerDetailDTO,
  CustomerListDTO as Api_PointmanCustomerListDTO,
  CustomerParams as Api_PointmanCustomerParams
} from '@call-point/relate-cx-common/dist/documents/customer/customer.api.schema';

import type {
  PhoneCallDetailDTO as Api_PhoneCallDetailDTO,
  PhoneCallListDTO as Api_PhoneCallListDTO,
  PhoneCallParams as Api_PhoneCallParams
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.api.schema';
import type {
  AccountUserParams as Api_AccountUserParams,
  AccountUserDetailDTO as Api_AccountUserDetailDTO
} from '@call-point/relate-cx-common/dist/documents/user/accountUser.api.schema';
import type { CustomerLocationListParams } from '@call-point/relate-cx-common/dist/provider/poolBrain/location/location.schema';
import type { Get, OverrideProperties, Simplify, SimplifyDeep } from 'type-fest';

// --- Service Titan Connect Types --- //
export { ST } from '@call-point/relate-cx-common/dist/libs';

export { PAGING_PARAM_KEYS } from '@call-point/relate-cx-common/dist/api/common.queryParam.types';

/**
 * UI Paging Params: narrowed version of Api Paging Params
 */
export type PagingParams = OverrideProperties<Api_PagingParams, { page?: number; pageSize?: number }>;

export type AccountQueryParams = Partial<OverrideProperties<Api_AccountParams, PagingParams>>;
export type AccountUserQueryParams = Partial<OverrideProperties<Api_AccountUserParams, PagingParams>>;
export type AiCallQueryParams = Partial<OverrideProperties<Api_AiCallParams, PagingParams>>;
export type AppointmentQueryParams = Partial<OverrideProperties<Api_AppointmentParams, PagingParams>>;
export type AppointmentWindowQueryParams = Partial<OverrideProperties<Api_AppointmentWindowParams, PagingParams>>;
export type AppointmentHistoryQueryParams = Partial<OverrideProperties<Api_AppointmentHistoryParams, PagingParams>>;
export type CallCenterQueryParams = Partial<OverrideProperties<Api_CallCenterParams, PagingParams>>;
export type CompanyQueryParams = Partial<OverrideProperties<Api_CompanyParams, PagingParams>>;
export type CompanyWindowQueryParams = Partial<OverrideProperties<Api_CompanyWindowParams, PagingParams>>;
export type PhoneCallQueryParams = Partial<OverrideProperties<Api_PhoneCallParams, PagingParams>>;
export type UserQueryParams = Partial<OverrideProperties<Api_UserParams, PagingParams>>;
export type PointmanCustomerParams = Partial<OverrideProperties<Api_PointmanCustomerParams, PagingParams>>;
export type CallSegmentQueryParams = Api_CallSegmentListParams;

// -- API General Types -- //

export { AppNameEnum } from '@call-point/relate-cx-common/dist/app/app.types';

export { HEADER_APP_NAME, HEADER_APP_VERSION } from '@call-point/relate-cx-common/dist/api/common.request.constants';

export {
  ConflictError,
  ConflictTypeEnum,
  FieldIssue,
  ForbiddenCodeEnum,
  ForbiddenResult,
  ValidationResult,
  UnsupportedAppVersionResult
} from '@call-point/relate-cx-common/dist/errors/api.error.types';

export { omit, pick } from '@call-point/relate-cx-common/dist/utils/object.utils';

export { arrayOfAll } from '@call-point/relate-cx-common/dist/utils/type.utils';

export { Permissions, PermissionScope } from '@call-point/relate-cx-common/dist/permissions/permission.schema';

export { _email, _zipCode, _enum } from '@call-point/relate-cx-common/dist/utils/schema.utils';

// --- API Resources --- //

/**
 * Object with common date properties for ease of porting an API type to a UI type since API dates are JS Date and
 * UI is currently using them as the pure ISO Date strings from the request.
 *
 * We should decide to either convert them back into Date objects via an Axios response interceptor OR we should create
 * the proper Types.
 *
 * TODO: any types we put into Redux should probably be converted to date strings!
 */
type CommonDateObj = {
  modifiedAt: string;
  createdAt: string;
};

export { IsActiveEnum } from '@call-point/relate-cx-common/dist/documents/base/base.document.types';

export { ListResult, ListResultWithoutPaging } from '@call-point/relate-cx-common/dist/api/common.request.types';
export {
  Paths,
  SetRequiredNonNullable,
  RequireAtLeastOne,
  NonEmptyArray,
  Nullable,
  PartialNullable,
  DeepPartialNullable,
  PartialDeep,
  SimpleZodOutput,
  ConstantArray
} from '@call-point/relate-cx-common/dist/common.types';

/**
 * Helper type to extract the value type of a nested path from an object.
 * Uses Get from type-fest to safely traverse nested properties.
 *
 * @example
 * type CompanyName = PathValue<CompanyUpdateApiDTO, 'name'>; // string
 * type VendorPhone = PathValue<CompanyUpdateApiDTO, 'callCenter.vendorPhoneNumber'>; // string | undefined
 */
export type PathValue<T, P extends Paths<T>> = Get<T, P>;

export { TradeEnum, TRADES } from '@call-point/relate-cx-common/dist/documents/document.types';

export {
  Address,
  AddressInput,
  AddressSchema,
  CountryEnum,
  CountryDisplayMap,
  UsStateOrProvinceDisplayMap,
  UsStateOrProvinceEnum,
  isUsStateOrProvinceEnum,
  CaStateOrProvinceDisplayMap,
  CaStateOrProvinceEnum,
  isCaStateOrProvinceEnum
} from '@call-point/relate-cx-common/dist/documents/address/address.types';

export { UPDATE_APP_VERSION_STATUS } from '@call-point/relate-cx-common/dist/app/app.types';

export type AccountResponseDTO = OverrideProperties<Api_AccountResponseDTO, CommonDateObj>;
export type {
  AccountCreateDTO,
  AccountUpdateDTO,
  AccountReactivateDTO
} from '@call-point/relate-cx-common/dist/documents/account/account.api.schema';
export {
  AccountCreateDtoSchema,
  AccountUpdateDtoSchema
} from '@call-point/relate-cx-common/dist/documents/account/account.api.schema';

type AppointmentDateObj = CommonDateObj & { date: string };

type AppointmentWindowStampedDateObj = { startTime: string; endTime: string };

type AppointmentDetailAppointmentWindow = OverrideProperties<
  Api_AppointmentDetailDTO['appointmentWindow'],
  AppointmentWindowStampedDateObj
>;
export type AppointmentDetailDTO = SimplifyDeep<
  OverrideProperties<
    Api_AppointmentDetailDTO,
    AppointmentDateObj & { appointmentWindow: AppointmentDetailAppointmentWindow }
  >
>;

type AppointmentListAppointmentWindow = OverrideProperties<
  Api_AppointmentListDTO['appointmentWindow'],
  AppointmentWindowStampedDateObj
>;
export type AppointmentListDTO = SimplifyDeep<
  OverrideProperties<
    Api_AppointmentListDTO,
    AppointmentDateObj & { appointmentWindow: AppointmentListAppointmentWindow }
  >
>;

type AppointmentHistoryAppointmentWindow = OverrideProperties<
  Api_AppointmentHistoryDetailDTO['appointmentWindow'],
  AppointmentWindowStampedDateObj
>;
export type AppointmentHistoryDetailDTO = OverrideProperties<
  Api_AppointmentHistoryDetailDTO,
  Omit<AppointmentDateObj, 'createdAt'> & { appointmentWindow: AppointmentHistoryAppointmentWindow }
>;

export { AppointmentStatusEnum } from '@call-point/relate-cx-common/dist/documents/appointment/appointment.repo.schema';
export {
  AppointmentCreateDTO,
  AppointmentAvailabilityDTO
} from '@call-point/relate-cx-common/dist/documents/appointment/appointment.api.schema';

export {
  BlockTypeEnum,
  RecurrenceTypeEnum
} from '@call-point/relate-cx-common/dist/documents/appointmentWindow/appointmentWindow.repo.schema';

type AppointmentWindowDateObj = Simplify<
  CommonDateObj & {
    startDate: string;
    endDate: string | null;
    startTime: string;
    endTime: string;
    parentEndDate: string | null;
  }
>;
export type AppointmentWindowListDTO = OverrideProperties<Api_AppointmentWindowListDTO, AppointmentWindowDateObj>;

export {
  LoginDTO,
  ChangePasswordDTO,
  ChangeOwnPasswordSchema,
  ChangePasswordSchema
} from '@call-point/relate-cx-common/dist/auth/auth.types';

export {
  getCallCenterCreateSchema,
  getCallCenterUpdateSchema
} from '@call-point/relate-cx-common/dist/documents/callCenter/callCenter.api.schema';
export type CallCenterDetailDTO = OverrideProperties<Api_CallCenterDetailDTO, CommonDateObj>;
export type CallCenterListDTO = OverrideProperties<Api_CallCenterListDTO, CommonDateObj>;
export type CallCenterCreateDTO = Api_CallCenterCreateDTO;
export type CallCenterUpdateDTO = Api_CallCenterUpdateDTO;

export type Company = OverrideProperties<Api_Company, CommonDateObj>;
export type ServiceTitanApiCompany = OverrideProperties<Api_ServiceTitanApiCompany, CommonDateObj>;
export type StCapacityCompany = OverrideProperties<Api_StCapacityCompany, CommonDateObj>;
export type ServiceTitanPortalV2Company = OverrideProperties<Api_ServiceTitanPortalV2Company, CommonDateObj>;
export type PoolBrainCompany = OverrideProperties<Api_PoolBrainCompany, CommonDateObj>;

export type {
  AppConfigDetailDTO,
  AppConfigAiVoiceListDTO,
  AppConfigUpdateDTO,
  NationalNumberGroupsUpdateDTO,
  ServiceTitanConfigUpdateDTO
} from '@call-point/relate-cx-common/dist/documents/appConfig/appConfig.api.schema';

export type {
  NationalNumber,
  NationalNumberGroups
} from '@call-point/relate-cx-common/dist/documents/appConfig/appConfig.repo.schema';

export type { AiVoice } from '@call-point/relate-cx-common/dist/documents/appConfig/appConfig.ai.schema';

export {
  AiCallAgentEnum,
  AiCallAgentDisplayMap,
  aiCallAgentValues
} from '@call-point/relate-cx-common/dist/documents/aiCall/aiCall.common.schemas';

type AiCallDateObj = Simplify<
  CommonDateObj & {
    startedAt: string;
    endedAt: string;
  }
>;
export type AiCallForCallPopDTO = OverrideProperties<
  Api_AiCallForCallPopDTO,
  { aiCall: OverrideProperties<Api_AiCallForCallPopDTO['aiCall'], AiCallDateObj>; company: Company }
>;
export type AiCallDetailDTO = OverrideProperties<Api_AiCallDetailDTO, AiCallDateObj>;
export type AiCallListDTO = OverrideProperties<Api_AiCallListDTO, Omit<AiCallDateObj, 'modifiedAt'>>;

export { AiCallUpdateDTO } from '@call-point/relate-cx-common/dist/documents/aiCall/aiCall.api.schema';

export { TranscriptMessage } from '@call-point/relate-cx-common/dist/documents/aiCall/aiCall.cache.schema';
export {
  completionStatuses,
  completionStatusDisplay,
  CompletionStatus,
  SipTransferIntent,
  SipTransferIntentMap,
  isSipTransferIntent,
  SipTransferIntentDisplayMap
} from '@call-point/relate-cx-common/dist/documents/aiCall/aiCall.utils';

export {
  getCompanyUpdateApiSchema,
  getCompanyCreateApiSchema,
  CompanyZipCodesUpdateDTO
} from '@call-point/relate-cx-common/dist/documents/company/company.api.schema';

export {
  DataProviderDetailDTO,
  DataProviderUpdateDTO,
  StCapacityDataProviderUpdateDTO,
  ServiceTitanPortalV2DataProviderUpdateDTO,
  PoolBrainDataProviderUpdateDTO,
  ServiceTitanApiTestConnectionDTO,
  DataProviderUpdateSchema,
  DataProviderV2UpdateSchema,
  PoolBrainDataProviderUpdateSchema,
  StCapacityDataProviderUpdateSchema,
  ServiceTitanApiTestConnectionSchema
} from '@call-point/relate-cx-common/dist/documents/company/dataProvider.api.schema';

export {
  CompanyContact,
  CompanyZone,
  CompanyZoneCallCenter,
  SisterCompany,
  CompanyCategoryEnum,
  allCompanyCategories,
  companyCategoryDisplayName,
  InvoicedServiceEnum,
  allInvoicedServices,
  isServiceTitanCompany,
  isServiceTitanApiCompany,
  isStCapacityCompany,
  isServiceTitanPortalCompany,
  isServiceTitanPortalV2Company,
  isPoolBrainCompany,
  isSwapiV1Company,
  isSwapiV2Company,
  ProviderVersions,
  ProviderCompany,
  ServiceTitanPortalV2DataProvider
} from '@call-point/relate-cx-common/dist/documents/company/company.repo.schema';
export type CompanyListDTO = OverrideProperties<Api_CompanyListDTO, CommonDateObj>;
export type CompanyDetailDTO = OverrideProperties<Api_CompanyDetailDTO, CommonDateObj>;
export type CompanyListNameDTO = Api_CompanyListNameDTO;
// TODO: fix this in common, reminder about the billing/settings existence issue
export type CompanyUpdateApiDTO = Api_CompanyUpdateApiDTO & Partial<AddressInput>;
export type CompanyCreateApiDTO = Api_CompanyCreateApiDTO;

// TODO: move to company.repo.schema
export type PointmanCompany = Simplify<Company & { dataProvider: null }>;

// TODO: move to company.repo.schema
export function isPointmanCompany<T extends PointmanCompany>(company: Company): company is T {
  return company.dataProvider === null;
}
export {
  CompanyWindowLabelEnum,
  CompanyWindowLabelDisplayMap,
  ServiceTypeEnum,
  WaivableStatusEnum,
  SERVICE_TYPES
} from '@call-point/relate-cx-common/dist/documents/companyWindow/companyWindow.types';
export {
  CompanyWindowCreateDTO,
  CompanyWindowUpdateDTO,
  companyWindowCreateApiSchema,
  CompanyWindowUpdateApiSchema
} from '@call-point/relate-cx-common/dist/documents/companyWindow/companyWindow.api.schema';

/** TODO: move to 'common' */
export const WAIVABLE_STATUSES = arrayOfAll<WaivableStatusEnum>()([
  WaivableStatusEnum.Never,
  WaivableStatusEnum.Standard
]);

export type CompanyWindowListDTO = OverrideProperties<Api_CompanyWindowListDTO, CommonDateObj>;
export type CompanyWindowDetailDTO = OverrideProperties<Api_CompanyWindowDetailDTO, CommonDateObj>;

export type PointmanCustomerDetailDTO = OverrideProperties<Api_PointmanCustomerDetailDTO, CommonDateObj>;
export type PointmanCustomerListDTO = OverrideProperties<Api_PointmanCustomerListDTO, CommonDateObj>;

export type {
  CustomerCreateDTO as PointmanCustomerCreateDTO,
  CustomerUpdateDTO as PointmanCustomerUpdateDTO
} from '@call-point/relate-cx-common/dist/documents/customer/customer.api.schema';
export {
  CustomerCreateSchema as PointmanCustomerCreateSchema,
  CustomerUpdateSchema as PointmanCustomerUpdateSchema
} from '@call-point/relate-cx-common/dist/documents/customer/customer.api.schema';

export { toDisplayName } from '@call-point/relate-cx-common/dist/documents/customer/customer.utils';

export {
  AgentTypeEnum,
  CallOutcomeEnum,
  activeCallOutcomes,
  isCallOutcome,
  isRequiredServiceTypeOutcome,
  isRequiredTradeOutcome
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.repo.schema';

export {
  isEmailOutcome,
  isEmployeeAssignOutcome,
  isPreviousAppointmentOutcome,
  isNewAppointmentOutcome,
  isRequiredJobOutcome,
  isRequiredCustomerOutcome,
  isRequiredNotesOutcome,
  isRequiredZoneOutcome
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.api.schema';
export { isPhoneCallCompletedError } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.api.utils';

export {
  isServiceTitanApiPhoneCall,
  isServiceTitanPortalV1PhoneCall,
  isServiceTitanPortalV2PhoneCall,
  isPoolBrainPhoneCall,
  isStCapacityPhoneCall,
  isSwapiV1PhoneCall,
  isSwapiV2PhoneCall
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.schema.utils';

export {
  ServiceTitanApiPhoneCallProviderData,
  ServiceTitanPortalV2PhoneCallProviderData,
  ServiceTitanPhoneCallCustomerMembership
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.serviceTitan.schema';

export {
  ServiceTitanApiPhoneCallCreateSchema,
  transformAssignedEmployee
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.serviceTitanApi.schema';
export type { ServiceTitanApiPhoneCallCreateDTO } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.serviceTitanApi.schema';

export { StCapacityPhoneCallCreateCtxSchema } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.stCapacity.schema';
export type { StCapacityPhoneCallCreateDTO } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.stCapacity.schema';

export { PoolBrainPhoneCallCreateCtxSchema } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.poolBrain.schema';
export type { PoolBrainPhoneCallCreateDTO } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.poolBrain.schema';

export { PointmanPhoneCallCreateSchema } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.pointman.schema';
export type { PointmanPhoneCallCreateDTO } from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.pointman.schema';

type PhoneCallDateObj = CommonDateObj & { appointmentDate: string | null; previousAppointmentDateTime: string | null };
export type PhoneCallDetailDTO = OverrideProperties<Api_PhoneCallDetailDTO, PhoneCallDateObj>;
export type PhoneCallListDTO = OverrideProperties<Api_PhoneCallListDTO, Pick<CommonDateObj, 'createdAt'>>;

export type CallSegmentListItemDTO = Omit<Api_CallSegmentListItemDTO, 'aiCall' | 'phoneCall'> &
  ({ aiCall: AiCallListDTO } | { phoneCall: PhoneCallListDTO });
export { CallSegmentAgentDisplayMap } from '@call-point/relate-cx-common/dist/documents/callSegment/callSegment.api.schema';

export {
  isStPortalRequiredJobOutcome,
  isStPortalRequiredAppointmentOutcome,
  ServiceTitanPortalPhoneCallCreateDTO,
  ServiceTitanPortalPhoneCallCreateSchema,
  ServiceTitanPortalReviewCallDTO
} from '@call-point/relate-cx-common/dist/documents/phoneCall/phoneCall.serviceTitanPortal.schema';

export {
  Note,
  NoteCreateDTO,
  NoteCreateSchema
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/note/note.types';

export {
  IanaTimezoneEnum,
  TimezoneAbbreviationEnum,
  SupportedTimezoneMap
} from '@call-point/relate-cx-common/dist/documents/company/timezone.types';

export {
  RoleEnum,
  User,
  NonServiceUser,
  isCallCenterUser,
  isAccountUser,
  isAccountAdmin,
  isSystemUser,
  canAccessCompany,
  UserTeamEnum,
  allUserTeams,
  INVALID_ROLE
} from '@call-point/relate-cx-common/dist/documents/user/user.repo.schema';

export type {
  UserListDTO,
  UserUpdateDTO,
  UserCreateDTO
} from '@call-point/relate-cx-common/dist/documents/user/user.api.schema';
export {
  UserUpdateDtoSchema,
  UserCreateDtoSchema
} from '@call-point/relate-cx-common/dist/documents/user/user.api.schema';
// TODO: decide if we want to keep the RoleEnum override here or not. Will CallPilot need to know about the Service role?
export type UserDetailDTO = OverrideProperties<
  Api_UserDetailDTO,
  CommonDateObj & { role: RoleEnum.USER | RoleEnum.ADMIN | RoleEnum.SYSTEM }
>;
export type AccountUserDetailDTO = OverrideProperties<Api_AccountUserDetailDTO, CommonDateObj>;
export type {
  AccountUserCreateDTO,
  AccountUserUpdateDTO
} from '@call-point/relate-cx-common/dist/documents/user/accountUser.api.schema';
export {
  AccountUserCreateSchema,
  AccountUserUpdateDtoSchema
} from '@call-point/relate-cx-common/dist/documents/user/accountUser.api.schema';
/**
 * --- API Provider Types ---
 */

export {
  SupportedProviderEnum,
  isSupportedProvider,
  PROVIDER_AUTH_ERROR_STATUS,
  PROVIDER_AUTH_ERROR_MESSAGE
} from '@call-point/relate-cx-common/dist/provider/provider.types';

/**
 * --- Service Titan API Resources ---
 */
export {
  DEFAULT_CAMPAIGN_NAME,
  OUTBOUND_CAMPAIGN_NAME
} from '@call-point/relate-cx-common/dist/provider/serviceTitan.provider.types';

export {
  AppointmentQueryParams as ServiceTitanAppointmentQueryParams,
  AdjustableCapacityParams as ServiceTitanAdjustableCapacityParams,
  BusinessUnitQueryParams as ServiceTitanBusinessUnitQueryParams,
  CustomerQueryParams as ServiceTitanCustomerQueryParams,
  LocationQueryParams as ServiceTitanLocationQueryParams,
  JobQueryParams as ServiceTitanJobQueryParams,
  JobHistoryQueryParams as ServiceTitanJobHistoryQueryParams,
  JobTypeQueryParams as ServiceTitanJobTypeQueryParams,
  ScheduledAppointmentQueryParams as ServiceTitanScheduledAppointmentQueryParams,
  TechnicianQueryParams as ServiceTitanTechnicianQueryParams,
  ZoneQueryParams as ServiceTitanZoneQueryParams
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/api/serviceTitan.queryParams.types';
export {
  ProviderListResult,
  ProviderListResultWithoutPaging,
  isProviderListResult
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/api/serviceTitan.request.types';

export {
  Appointment as ServiceTitanAppointment,
  ScheduledAppointment as ServiceTitanScheduledAppointment
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/appointment/appointment.types';

export { AdjustableCapacityDTO as ServiceTitanAdjustableCapacityDTO } from '@call-point/relate-cx-common/dist/provider/serviceTitan/capacity/adjustableCapacity.schema';

export { BusinessUnit as ServiceTitanBusinessUnit } from '@call-point/relate-cx-common/dist/provider/serviceTitan/businessUnit/businessUnit.types';

export {
  Contact,
  ContactDTO,
  ContactDtoSchema
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/contact/contact.types';

export {
  Customer,
  CustomerCreateDTO,
  CustomerCreateResponseDTO,
  CustomerCreateSchema,
  CustomerDetailDTO,
  CustomerPatchDTO,
  CustomerSchema,
  CustomerSearchDTO
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/customer/customer.types';

export { CustomerMembership } from '@call-point/relate-cx-common/dist/provider/serviceTitan/customer/customerMembership.types';

export { JobCancelEvent as ServiceTitanJobCancelEvent } from '@call-point/relate-cx-common/dist/provider/serviceTitan/job/job.event.schema';
export {
  Job as ServiceTitanJob,
  JobHistoryItem as ServiceTitanJobHistoryItem
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/job/job.types';
export { JobType as ServiceTitanJobType } from '@call-point/relate-cx-common/dist/provider/serviceTitan/jobType/jobType.types';

export { ServiceTitanMappedCall } from '@call-point/relate-cx-common/dist/provider/serviceTitan/call/call.schema';

export {
  Location,
  LocationCreateSchema,
  LocationCreateDTO,
  LocationDetailDTO,
  LocationPatchDTO,
  LocationSchema
} from '@call-point/relate-cx-common/dist/provider/serviceTitan/location/location.types';

export { MembershipType } from '@call-point/relate-cx-common/dist/provider/serviceTitan/membershipType/membershipType.types';
export { Technician } from '@call-point/relate-cx-common/dist/provider/serviceTitan/technician/technician.types';

export { Zone as ServiceTitanZone } from '@call-point/relate-cx-common/dist/provider/serviceTitan/zone/zone.schema';

/**
 * --- PoolBrain Resources ---
 */

export { ContactCustomerSchema as PoolBrainContactCustomerSchema } from '@call-point/relate-cx-common/dist/provider/poolBrain/customer/contactCustomer.schema';

export type PoolBrainCustomerLocationListParams = CustomerLocationListParams;

export type PoolBrainContactCustomerDTO = OverrideProperties<Api_ContactCustomerDTO, Pick<CommonDateObj, 'modifiedAt'>>;
export type PoolBrainCustomerLocationDTO = OverrideProperties<
  Api_CustomerLocationDTO,
  { customer: PoolBrainContactCustomerDTO }
>;

export type PoolBrainCustomerDetailDTO = OverrideProperties<Api_CustomerDetailDTO, Pick<CommonDateObj, 'modifiedAt'>>;

export type EmployeeDetailDTO = {
  firstName: string;
  lastName: string;
  id: string;
  providerId: string;
};
